import { useMemo, useState } from 'react';
import TextInputWithInlineLabel from '../TextInputWithInlineLabel.jsx';
import MagnifyingGlassIcon from '../icons/MagnifyingGlassIcon.jsx';
import Loading from '../Loading';
import DotDotDotMenu from '../DotDotDotMenu';
import Tooltip from '../Tooltip';
import LinkSlashIcon from '../icons/LinkSlashIcon';
import DetachThemeModal from './DetachThemeModal';
import {
    FORM_CSS_API_VERSION,
    FORM_ROOT_CLASS,
    FormPart,
} from '~/lib/FormCssApi';
import {
    ArrowDownTrayIcon,
    EyeSlashIcon,
    LockClosedIcon,
    PencilIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';

const DEFAULT_COLOR = '#2C87F3';

const shapeRadius = {
    square: 'rounded-none',
    'rounded-rectangle': 'rounded-lg',
    pill: 'rounded-full',
};

function radiusFor(shape) {
    return shapeRadius[shape] || shapeRadius['rounded-rectangle'];
}

// The tooltip renders its message as HTML, and a description is whatever the
//  theme's author typed — for a public theme, another business
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function isTransparent(color) {
    return !color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)';
}

/**
 * The color a theme's submit button really comes out in, and whether it is
 *  filled or just outlined.
 *
 * Custom CSS can recolor the button any number of ways — a background on the
 *  submit part, a border, or a new --breely-color — so rather than reading the
 *  CSS, this lets the browser apply it: a bare submit button is rendered out of
 *  sight inside a shadow root, where the theme's served CSS is the only styling
 *  that reaches it, and the result is read back off it. Without custom CSS
 *  that comes out as the theme's color.
 */
function themeButtonStyle(settings, servedCss, isOutline) {
    const accent = settings?.color || DEFAULT_COLOR;
    const fallback = { color: accent, isFilled: !isOutline, textColor: '#fff' };

    if (!servedCss) {
        return fallback;
    }

    const host = document.createElement('div');
    host.style.cssText =
        'position:absolute;visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden';

    try {
        document.body.appendChild(host);
        const root = host.attachShadow({ mode: 'open' });

        // On the host so that a --breely-color set on the form root by the
        //  custom CSS wins, just as it would on the real form. The browser's
        //  own button styling — a gray fill and border — is cleared first, at
        //  no specificity so that any rule in the custom CSS beats it, or it
        //  would read as the custom CSS having colored the button. Its white
        //  text is set on the button itself, likewise at no specificity, so
        //  custom CSS recoloring the submit part wins but a text color set on
        //  the form as a whole doesn't reach it
        const style = document.createElement('style');
        style.textContent = `:host { --breely-color: ${accent}; }\n:where(button) { all: unset; color: #fff; }\n${servedCss}`;
        root.appendChild(style);

        const form = document.createElement('div');
        form.className = FORM_ROOT_CLASS;
        form.setAttribute('data-breely-css-version', `${FORM_CSS_API_VERSION}`);
        form.setAttribute('data-breely-state', 'filling');
        const button = document.createElement('button');
        button.type = 'submit';
        button.setAttribute('data-breely-part', FormPart.SUBMIT);
        button.textContent = 'Submit';
        form.appendChild(button);
        root.appendChild(form);

        const computed = getComputedStyle(button);

        if (!isTransparent(computed.backgroundColor)) {
            return {
                color: computed.backgroundColor,
                isFilled: true,
                textColor: computed.color,
            };
        }

        if (
            computed.borderTopStyle !== 'none' &&
            parseFloat(computed.borderTopWidth) > 0 &&
            !isTransparent(computed.borderTopColor)
        ) {
            return {
                color: computed.borderTopColor,
                isFilled: false,
                // The default text is white, made for a filled button, so an
                //  outline is labeled in its own color as on the real form
                textColor: computed.borderTopColor,
            };
        }

        const variable = computed.getPropertyValue('--breely-color').trim();

        return {
            color: variable || accent,
            isFilled: !isOutline,
            textColor: '#fff',
        };
    } catch {
        // No shadow DOM to measure in, so go by the theme's own color
        return fallback;
    } finally {
        host.remove();
    }
}

// Matches the marketing site's gallery, so a theme looks the same in both
const fontFamilies = {
    sans: 'theme-font-sans',
    serif: 'theme-font-serif',
};

const buttonTextSizes = {
    small: 'text-[10px]',
    normal: 'text-[11px]',
    large: 'text-[12px]',
    'extra-large': 'text-[13px]',
};

const headingTextSizes = {
    small: 'text-[12px]',
    normal: 'text-[13px]',
    large: 'text-[14px]',
    'extra-large': 'text-[15px]',
};

/**
 * A miniature of what a form styled this way looks like: a card with the
 * theme's name, two inputs and a button, in the theme's font, size, color and
 * shapes, on a background tinted with its color. Enough to tell two themes
 * apart at a glance, which is all a tile has room for.
 */
const ThemePreview = ({ settings, servedCss, name }) => {
    const button = settings?.button || 'rounded-rectangle';
    const isOutlineButton = button.startsWith('inverse-');
    const buttonShape = button.replace(/^inverse-/, '');
    const fontSize = settings?.fontSize || 'normal';

    const { color, isFilled, textColor } = useMemo(
        () => themeButtonStyle(settings, servedCss, isOutlineButton),
        [settings, servedCss, isOutlineButton],
    );

    return (
        <div
            className={`aspect-[8/5] flex items-center justify-center p-3 ${fontFamilies[settings?.font] || fontFamilies.sans}`}
            style={{
                backgroundColor: `color-mix(in srgb, ${color} 14%, #F2F2F2)`,
            }}
        >
            <div className="w-[78%] min-w-[120px] flex flex-col gap-2 p-3.5 bg-white rounded-lg shadow-[0_1px_2px_rgba(30,58,95,0.08),0_4px_8px_rgba(30,58,95,0.06)]">
                {!!name && (
                    <div
                        className={`${headingTextSizes[fontSize] || headingTextSizes.normal} font-semibold leading-tight text-gray-900 truncate mb-0.5`}
                    >
                        {name}
                    </div>
                )}
                <div
                    className={`h-[22px] border border-gray-200 ${radiusFor(settings?.input)}`}
                />
                <div
                    className={`h-[22px] border border-gray-200 ${radiusFor(settings?.input)}`}
                />
                <div
                    className={`self-center mt-1 px-3 py-1.5 border-[1.5px] font-semibold leading-none ${buttonTextSizes[fontSize] || buttonTextSizes.normal} ${radiusFor(buttonShape)}`}
                    style={
                        isFilled
                            ? {
                                  backgroundColor: color,
                                  borderColor: color,
                                  color: textColor,
                              }
                            : { borderColor: color, color }
                    }
                >
                    Submit
                </div>
            </div>
        </div>
    );
};

/**
 * One theme in the grid. The tile is the control: the whole thing is clickable,
 * and what it is offering shows over the preview — "Current theme" on the one
 * already in use, "Use theme" on the others while they are hovered or focused.
 *
 * The tile's menu sits outside that button rather than inside it, because a
 * control nested in a control is neither valid nor clickable, and outside its
 * rounded clipping so the menu it opens is not cut off by the tile.
 */
const ThemeTile = ({
    theme,
    isCurrent,
    onApply,
    onDelete,
    onPublish,
    onUnpublish,
    onEdit,
    onDetach,
    isSaving,
}) => {
    return (
        // Lifting it makes the tile a stacking context of its own, so it's
        //  raised above the tiles after it too, or the menu it opens would
        //  slide under them
        <div
            className={`group relative transition-transform duration-200 ease-out hover:z-10 focus-within:z-10 ${
                isCurrent ? '' : 'motion-safe:hover:-translate-y-0.5'
            }`}
        >
            <button
                type="button"
                className={`group/tile relative block w-full text-left rounded-xl overflow-hidden transition-shadow duration-200 ease-out shadow-[0_1px_2px_rgba(30,58,95,0.06),0_2px_6px_rgba(30,58,95,0.05)] ${
                    isCurrent
                        ? ''
                        : 'group-hover:shadow-[0_2px_4px_rgba(30,58,95,0.12),0_8px_16px_rgba(30,58,95,0.1)]'
                }`}
                onClick={() => (isCurrent ? null : onApply(theme.id))}
                disabled={isSaving || isCurrent}
                aria-current={isCurrent ? 'true' : undefined}
            >
                <div className="relative">
                    <ThemePreview
                        settings={theme.settings}
                        servedCss={theme.servedCss}
                        name={theme.name}
                    />

                    {/* Tucked into the corner as a tag, so the preview it
                        labels stays in view */}
                    <span
                        className={`absolute top-0 left-0 bg-primary-700 text-white text-[10px] font-semibold uppercase tracking-[0.2px] leading-none px-3 py-2 rounded-br-lg ${
                            isCurrent
                                ? ''
                                : 'opacity-0 group-hover:opacity-100 group-focus-visible/tile:opacity-100 transition-opacity'
                        }`}
                    >
                        {isCurrent ? 'Current theme' : 'Use theme'}
                    </span>
                </div>

                <div className="bg-white px-4 py-3">
                    {theme.description ? (
                        <Tooltip
                            message={escapeHtml(theme.description)}
                            tooltipId={`theme-description-${theme.id}`}
                            tooltipClassName="!text-xs !px-2 !py-1 !max-w-60 whitespace-pre-line"
                            className="text-xs text-gray-700 truncate"
                        >
                            {theme.name}
                        </Tooltip>
                    ) : (
                        <div className="text-xs text-gray-700 truncate">
                            {theme.name}
                        </div>
                    )}
                    {!!theme.author && (
                        <div className="text-xs text-gray-500 truncate">
                            by {theme.author}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-x-2 text-xs text-gray-500 mt-1">
                        {theme.isPublic ? (
                            <div>Public</div>
                        ) : theme.isUnpublished ? (
                            <div className="flex items-center gap-x-1">
                                <EyeSlashIcon className="w-3.5 h-3.5 shrink-0" />
                                Unpublished
                            </div>
                        ) : (
                            <div className="flex items-center gap-x-1">
                                <LockClosedIcon className="w-3.5 h-3.5 shrink-0" />
                                Private
                            </div>
                        )}
                        {/* Only a theme that has been public can be used
                            beyond the business that made it, so only there is
                            the count telling */}
                        {(theme.isPublic || theme.isUnpublished) && (
                            <Tooltip
                                message={`Used by ${theme.businessesUsing ?? 0} ${theme.businessesUsing === 1 ? 'business' : 'businesses'}`}
                                tooltipId={`theme-usage-${theme.id}`}
                                tooltipClassName="!text-xs !px-2 !py-1"
                                className="flex items-center gap-x-1 shrink-0"
                            >
                                <ArrowDownTrayIcon className="w-3.5 h-3.5 shrink-0" />
                                {theme.businessesUsing ?? 0}
                            </Tooltip>
                        )}
                    </div>
                </div>

                {/* The border is drawn over the tile rather than around it, so
                    it covers the clipped edge of the corner tag instead of
                    leaving a sliver of background between the two */}
                <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-0 rounded-xl border-[1.5px] transition-colors ${
                        isCurrent
                            ? 'border-primary-700'
                            : 'border-transparent group-hover:border-primary-700 group-focus-visible/tile:border-primary-700'
                    }`}
                />
            </button>

            {!!(
                theme.isEditable ||
                theme.canDelete ||
                theme.canPublish ||
                theme.canUnpublish ||
                isCurrent
            ) && (
                <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <DotDotDotMenu
                        btnClassName="flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-600 hover:text-gray-900 shadow-sm focus:outline-none"
                        menuItemsClassName="!w-auto min-w-48 whitespace-nowrap"
                        options={[
                            theme.isEditable && {
                                name: 'Edit',
                                icon: <PencilIcon className="w-4 h-4" />,
                                className: 'gap-x-6',
                                onClick: () => {
                                    // After the menu has finished closing, as
                                    //  with Make Public below
                                    setTimeout(() => onEdit(theme), 100);

                                    return true;
                                },
                            },
                            theme.canPublish && {
                                name: 'Make Public',
                                icon: <UserGroupIcon className="w-4 h-4" />,
                                className: 'gap-x-6',
                                onClick: () => {
                                    // After the menu has finished closing, so
                                    //  the modal isn't opened while the menu
                                    //  still holds focus
                                    setTimeout(() => onPublish(theme), 100);

                                    return true;
                                },
                            },
                            theme.canUnpublish && {
                                name: 'Unpublish',
                                icon: <EyeSlashIcon className="w-4 h-4" />,
                                className: 'gap-x-6',
                                onClick: () => {
                                    if (
                                        confirm(
                                            `Unpublish the "${theme.name}" theme?\n\nIt will no longer be offered to other businesses. Forms already using it keep it, and it still cannot be edited. You can make it public again later.`,
                                        )
                                    ) {
                                        onUnpublish(theme);
                                    }

                                    // Closes the menu either way
                                    return true;
                                },
                            },
                            // Only the theme the form is on can be detached
                            //  from, whoever's theme it is
                            isCurrent && {
                                name: 'Detach Theme',
                                icon: <LinkSlashIcon className="w-4 h-4" />,
                                className: 'gap-x-6',
                                onClick: () => {
                                    // After the menu has finished closing, as
                                    //  with Make Public above
                                    setTimeout(onDetach, 100);

                                    return true;
                                },
                            },
                            theme.canDelete && {
                                name: 'Delete Theme',
                                isDelete: true,
                                // The row is a flex row of label then icon, and
                                //  shrink-wrapped here, so they would otherwise
                                //  sit flush against each other
                                className: 'gap-x-6',
                                onClick: () => {
                                    if (
                                        confirm(
                                            `Are you sure you want to delete the "${theme.name}" theme?\n\nForms using it keep the way they look now and stop following the theme. This cannot be undone.`,
                                        )
                                    ) {
                                        onDelete(theme.id);
                                    }

                                    // Closes the menu either way
                                    return true;
                                },
                            },
                        ].filter(Boolean)}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Choosing which theme a form uses.
 *
 * Applying one takes its look wholesale — anything the form was overriding is
 * cleared — so this is the one place in the panel where a click changes how the
 * form looks all at once.
 */
const StylesThemePicker = ({
    themes = [],
    isLoading,
    appliedTheme,
    onApply,
    onDetach,
    onDelete,
    onPublish,
    onUnpublish,
    onEdit,
    onSaveAsNewTheme,
    isSaving,
}) => {
    const [search, setSearch] = useState('');
    const [isConfirmingDetach, setIsConfirmingDetach] = useState(false);

    const term = search.trim().toLowerCase();
    // The business's own private themes come first. The sort is stable, so
    // each group keeps the server's alphabetical order.
    const matching = (
        term
            ? themes.filter(t => t.name.toLowerCase().includes(term))
            : [...themes]
    ).sort((a, b) => Number(a.isPublic) - Number(b.isPublic));

    return (
        <div>
            {!isLoading && themes.length > 0 && (
                <TextInputWithInlineLabel
                    value={search}
                    onChange={setSearch}
                    placeholder="Search themes..."
                    inputClassName="pl-10"
                    className="mb-6"
                    prefix={
                        <MagnifyingGlassIcon className="w-5 h-5 text-primary-700" />
                    }
                />
            )}

            {isLoading && <Loading />}

            {!isLoading && themes.length > 0 && (
                <div>
                    <div className="panel-label font-semibold">All themes</div>

                    {matching.length === 0 ? (
                        <div className="text-gray-500">
                            No themes match &ldquo;{search.trim()}&rdquo;.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {matching.map(theme => (
                                <ThemeTile
                                    key={theme.id}
                                    theme={theme}
                                    isCurrent={theme.id === appliedTheme?.id}
                                    onApply={onApply}
                                    onDelete={onDelete}
                                    onPublish={onPublish}
                                    onUnpublish={onUnpublish}
                                    onEdit={onEdit}
                                    onDetach={() => setIsConfirmingDetach(true)}
                                    isSaving={isSaving}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!isLoading && themes.length === 0 && (
                <div className="text-center px-4 py-6">
                    <div className="text-gray-700">No themes yet</div>
                    <div className="text-gray-500 text-sm mt-1 mb-5">
                        Save how this form looks as a theme, and you can use the
                        same look on your other forms.
                    </div>
                    <button
                        type="button"
                        className="btn-pill"
                        onClick={onSaveAsNewTheme}
                        disabled={isSaving}
                    >
                        Save as New Theme
                    </button>
                </div>
            )}

            <DetachThemeModal
                isOpen={isConfirmingDetach}
                isSaving={isSaving}
                onClose={() => setIsConfirmingDetach(false)}
                onConfirm={() => {
                    setIsConfirmingDetach(false);
                    onDetach();
                }}
            />
        </div>
    );
};

export { ThemePreview };

export default StylesThemePicker;
